/**
 * IdentityVerificationModal.jsx
 * Il Diario di Bordo - Admin Panel
 *
 * Modale avanzato per verifica identità coordinatori.
 * Include:
 * - Tab Verifica: 6 domande con valore atteso visibile + campo input
 * - Tab Storico: Audit trail verifiche precedenti
 * - Tab Azione: Override + Download PDF
 * 
 * Layout responsive: desktop (affiancati) / mobile (uno sotto l'altro)
 */

import { useState, useEffect } from 'react'
import {
  X, Loader2, CheckCircle, XCircle, AlertTriangle, HelpCircle,
  FileText, Download, History, Shield, ShieldOff, Phone, Mail,
  Video, UserCheck, RefreshCw, Check, Minus
} from 'lucide-react'
import { adminApi } from '../services/api'
import api from '../services/api'

// Tab disponibili
const TABS = [
  { id: 'verify', label: 'Verifica', icon: Shield },
  { id: 'history', label: 'Storico', icon: History },
  { id: 'action', label: 'Azione', icon: FileText }
]

// Metodi di contatto
const CONTACT_METHODS = [
  { value: 'phone', label: 'Telefono', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'video', label: 'Videochiamata', icon: Video },
  { value: 'in_person', label: 'Di persona', icon: UserCheck }
]

// Badge risultato
const ResultBadge = ({ result }) => {
  const styles = {
    correct: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    partial: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    incorrect: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }
  
  const labels = {
    correct: 'Corretta',
    partial: 'Parziale',
    incorrect: 'Errata',
    pending: 'In attesa'
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[result] || styles.pending}`}>
      {labels[result] || 'In attesa'}
    </span>
  )
}

// Componente per confronto real-time risposta
const MatchIndicator = ({ expected, provided, matchType }) => {
  if (!provided || !provided.trim()) {
    return (
      <span className="text-xs text-gray-400 flex items-center gap-1">
        <Minus className="h-3 w-3" />
        In attesa di risposta
      </span>
    )
  }

  // Confronto semplificato lato client (il vero confronto è server-side)
  const normalizedExpected = String(expected || '').toLowerCase().trim()
  const normalizedProvided = provided.toLowerCase().trim()
  
  let isMatch = false
  let isPartial = false

  if (matchType === 'exact') {
    isMatch = normalizedExpected === normalizedProvided
  } else if (matchType === 'fuzzy' || matchType === 'partial') {
    isMatch = normalizedExpected === normalizedProvided
    isPartial = !isMatch && (normalizedExpected.includes(normalizedProvided) || normalizedProvided.includes(normalizedExpected))
  } else {
    // Per date e numeri, accetta se simile
    isMatch = normalizedExpected === normalizedProvided
    isPartial = !isMatch && normalizedProvided.length > 0
  }

  if (isMatch) {
    return (
      <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
        <Check className="h-3 w-3" />
        Corrispondenza esatta
      </span>
    )
  } else if (isPartial) {
    return (
      <span className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
        <Minus className="h-3 w-3" />
        Possibile corrispondenza parziale
      </span>
    )
  } else {
    return (
      <span className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
        <X className="h-3 w-3" />
        Non corrispondente
      </span>
    )
  }
}

export default function IdentityVerificationModal({ 
  isOpen, 
  onClose, 
  user,
  onSuccess 
}) {
  // State
  const [activeTab, setActiveTab] = useState('verify')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  
  // Dati verifica
  const [verificationData, setVerificationData] = useState(null)
  const [answers, setAnswers] = useState({})
  const [contactMethod, setContactMethod] = useState('phone')
  const [declaredReason, setDeclaredReason] = useState('')
  const [verifierNotes, setVerifierNotes] = useState('')
  
  // Risultato
  const [result, setResult] = useState(null)
  
  // Storico
  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  
  // Override
  const [showOverride, setShowOverride] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')

  // Download PDF
  const [downloadingPDF, setDownloadingPDF] = useState(false)

  // Carica dati iniziali
  useEffect(() => {
    if (isOpen && user?.id) {
      loadVerificationData()
      setActiveTab('verify')
      setResult(null)
      setError('')
    }
  }, [isOpen, user?.id])

  // Carica storico quando si passa al tab
  useEffect(() => {
    if (activeTab === 'history' && history.length === 0 && user?.id) {
      loadHistory()
    }
  }, [activeTab])

  const loadVerificationData = async () => {
    try {
      setLoading(true)
      setError('')
      
      const response = await adminApi.startIdentityVerification(user.id)
      setVerificationData(response.data.data)
      
      // Inizializza risposte vuote
      const initialAnswers = {}
      response.data.data.questions.forEach(q => {
        initialAnswers[q.questionId] = ''
      })
      setAnswers(initialAnswers)
      
    } catch (err) {
      console.error('Load verification data error:', err)
      setError(err.response?.data?.message || 'Errore nel caricamento dei dati')
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    try {
      setLoadingHistory(true)
      const response = await adminApi.getIdentityVerificationHistory(user.id)
      setHistory(response.data.data.verifications || [])
    } catch (err) {
      console.error('Load history error:', err)
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }))
  }

  const handleSubmit = async () => {
    // Validazione
    if (!contactMethod) {
      setError('Seleziona il metodo di contatto')
      return
    }
    if (!declaredReason.trim()) {
      setError('Inserisci il motivo dichiarato dall\'utente')
      return
    }
    
    const emptyAnswers = Object.values(answers).filter(a => !a.trim()).length
    if (emptyAnswers > 0) {
      setError(`Compila tutte le risposte (${emptyAnswers} mancanti)`)
      return
    }

    try {
      setSubmitting(true)
      setError('')

      const answersArray = Object.entries(answers).map(([questionId, providedAnswer]) => ({
        questionId,
        providedAnswer
      }))

      const response = await adminApi.submitIdentityVerification({
        userId: user.id,
        answers: answersArray,
        contactMethod,
        declaredReason,
        verifierNotes: verifierNotes || null
      })

      setResult(response.data.data)
      setActiveTab('action')

      // Se approvato, notifica il parent
      if (response.data.data.verification.approved) {
        onSuccess?.()
      }

    } catch (err) {
      console.error('Submit verification error:', err)
      setError(err.response?.data?.message || 'Errore nella verifica')
    } finally {
      setSubmitting(false)
    }
  }

  const handleOverride = async () => {
    if (!overrideReason.trim() || overrideReason.length < 10) {
      setError('Motivazione override richiesta (minimo 10 caratteri)')
      return
    }

    try {
      setSubmitting(true)
      setError('')

      // Ri-submit con override
      const answersArray = Object.entries(answers).map(([questionId, providedAnswer]) => ({
        questionId,
        providedAnswer
      }))

      const response = await adminApi.submitIdentityVerification({
        userId: user.id,
        answers: answersArray,
        contactMethod,
        declaredReason,
        verifierNotes,
        override: {
          enabled: true,
          reason: overrideReason
        }
      })

      setResult(response.data.data)
      setShowOverride(false)
      onSuccess?.()

    } catch (err) {
      console.error('Override error:', err)
      setError(err.response?.data?.message || 'Errore nell\'override')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDownloadPDF = async (reportId) => {
    try {
      setDownloadingPDF(true)
      
      // Usa axios per scaricare con autenticazione
      const response = await api.get(`/admin/2fa/identity-verification/report/${reportId}/pdf`, {
        responseType: 'blob'
      })
      
      // Crea URL blob e scarica
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `report-verifica-${reportId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
    } catch (err) {
      console.error('Download PDF error:', err)
      setError('Errore nel download del PDF')
    } finally {
      setDownloadingPDF(false)
    }
  }

  // Formatta valore atteso per visualizzazione
  const formatExpectedValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'Nessun dato disponibile'
    }
    return value || 'Non disponibile'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center space-x-3">
            <Shield className="h-6 w-6 text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">
                Verifica Identità per Reset 2FA
              </h2>
              <p className="text-sm text-blue-100">
                {user?.nome} {user?.cognome} ({user?.email})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex space-x-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
              <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
              <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Caricamento...</span>
            </div>
          ) : (
            <>
              {/* Tab Verify */}
              {activeTab === 'verify' && verificationData && (
                <div className="space-y-6">
                  {/* Info utente */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                      Informazioni Coordinatore
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-600 dark:text-blue-400">Nome:</span>
                        <span className="ml-2 text-blue-900 dark:text-blue-100">{verificationData.user.nome} {verificationData.user.cognome}</span>
                      </div>
                      <div>
                        <span className="text-blue-600 dark:text-blue-400">Email:</span>
                        <span className="ml-2 text-blue-900 dark:text-blue-100">{verificationData.user.email}</span>
                      </div>
                      <div>
                        <span className="text-blue-600 dark:text-blue-400">Centro:</span>
                        <span className="ml-2 text-blue-900 dark:text-blue-100">{verificationData.tenant?.name}</span>
                      </div>
                      <div>
                        <span className="text-blue-600 dark:text-blue-400">Ruolo:</span>
                        <span className="ml-2 text-blue-900 dark:text-blue-100 capitalize">{verificationData.user.role}</span>
                      </div>
                    </div>
                  </div>

                  {/* Metodo contatto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Come hai contattato l'utente? *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {CONTACT_METHODS.map(method => (
                        <button
                          key={method.value}
                          onClick={() => setContactMethod(method.value)}
                          className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all ${
                            contactMethod === method.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <method.icon className="h-5 w-5 mr-2" />
                          {method.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Motivo dichiarato */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Motivo dichiarato dall'utente *
                    </label>
                    <textarea
                      value={declaredReason}
                      onChange={(e) => setDeclaredReason(e.target.value)}
                      placeholder="Es: Ha perso l'accesso al telefono con l'app authenticator..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={2}
                    />
                  </div>

                  {/* Domande */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Domande di Verifica ({verificationData.questions.length})
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Chiedi all'utente queste informazioni e confronta le risposte con i valori attesi. 
                      Soglia minima: 5 risposte corrette su 6.
                    </p>
                    
                    <div className="space-y-4">
                      {verificationData.questions.map((question, idx) => (
                        <div key={question.questionId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                          {/* Header domanda */}
                          <div className="flex items-start justify-between mb-3">
                            <label className="font-medium text-gray-800 dark:text-gray-200">
                              {idx + 1}. {question.questionLabel}
                            </label>
                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <HelpCircle className="h-3 w-3 mr-1" />
                              {question.toleranceDescription}
                            </div>
                          </div>
                          
                          {/* Container campi - responsive */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Valore atteso */}
                            <div>
                              <label className="block text-xs font-medium text-green-700 dark:text-green-400 mb-1 flex items-center">
                                <Check className="h-3 w-3 mr-1" />
                                Valore atteso
                              </label>
                              <div className="px-4 py-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200 font-mono">
                                {formatExpectedValue(question.expectedValue)}
                              </div>
                            </div>
                            
                            {/* Campo input risposta */}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                                ✎ Risposta utente
                              </label>
                              <input
                                type="text"
                                value={answers[question.questionId] || ''}
                                onChange={(e) => handleAnswerChange(question.questionId, e.target.value)}
                                placeholder="Inserisci la risposta dell'utente..."
                                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                              />
                            </div>
                          </div>
                          
                          {/* Indicatore corrispondenza */}
                          <div className="mt-2">
                            <MatchIndicator 
                              expected={formatExpectedValue(question.expectedValue)}
                              provided={answers[question.questionId]}
                              matchType={question.matchType}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Note verificatore */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Note del verificatore (opzionale)
                    </label>
                    <textarea
                      value={verifierNotes}
                      onChange={(e) => setVerifierNotes(e.target.value)}
                      placeholder="Eventuali note o osservazioni..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* Tab History */}
              {activeTab === 'history' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Storico Verifiche
                    </h3>
                    <button
                      onClick={loadHistory}
                      disabled={loadingHistory}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${loadingHistory ? 'animate-spin' : ''}`} />
                      Aggiorna
                    </button>
                  </div>

                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <History className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <p>Nessuna verifica precedente</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {history.map((item, idx) => (
                        <div
                          key={idx}
                          className={`border rounded-lg p-4 ${
                            item.approved
                              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                              : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              {item.approved ? (
                                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-500 mr-2" />
                              )}
                              <span className="font-medium">
                                {item.approved ? 'Approvata' : 'Rifiutata'}
                                {item.override?.enabled && ' (Override)'}
                              </span>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(item.verifiedAt).toLocaleString('it-IT')}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Punteggio:</span>
                              <span className="ml-2 font-medium">{item.score?.correct || 0}/{item.score?.total || 6}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Metodo:</span>
                              <span className="ml-2">{item.contactMethod || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Verificatore:</span>
                              <span className="ml-2">{item.verifiedBy?.email || '-'}</span>
                            </div>
                          </div>

                          {item.declaredReason && (
                            <div className="mt-2 text-sm">
                              <span className="text-gray-500">Motivo:</span>
                              <span className="ml-2">{item.declaredReason}</span>
                            </div>
                          )}

                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => handleDownloadPDF(item.reportId)}
                              disabled={downloadingPDF}
                              className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                            >
                              {downloadingPDF ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4 mr-1" />
                              )}
                              Scarica PDF
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab Action */}
              {activeTab === 'action' && (
                <div className="space-y-6">
                  {result ? (
                    <>
                      {/* Risultato verifica */}
                      <div className={`rounded-lg p-6 ${
                        result.verification.approved
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-center mb-4">
                          {result.verification.approved ? (
                            <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                          ) : (
                            <XCircle className="h-8 w-8 text-red-500 mr-3" />
                          )}
                          <div>
                            <h3 className={`text-lg font-semibold ${
                              result.verification.approved
                                ? 'text-green-800 dark:text-green-200'
                                : 'text-red-800 dark:text-red-200'
                            }`}>
                              Verifica {result.verification.approved ? 'Superata' : 'Non Superata'}
                              {result.verification.override?.enabled && ' (Override)'}
                            </h3>
                            <p className={`text-sm ${
                              result.verification.approved
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              Report ID: {result.verification.reportId}
                            </p>
                          </div>
                        </div>

                        {/* Punteggio */}
                        <div className="grid grid-cols-4 gap-4 mb-4">
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <p className="text-2xl font-bold text-green-600">{result.score.correct}</p>
                            <p className="text-xs text-gray-500">Corrette</p>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <p className="text-2xl font-bold text-yellow-600">{result.score.partial}</p>
                            <p className="text-xs text-gray-500">Parziali</p>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <p className="text-2xl font-bold text-red-600">{result.score.incorrect}</p>
                            <p className="text-xs text-gray-500">Errate</p>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <p className="text-2xl font-bold text-blue-600">{result.score.percentage}%</p>
                            <p className="text-xs text-gray-500">Punteggio</p>
                          </div>
                        </div>

                        {/* Dettaglio risposte */}
                        <div className="space-y-2">
                          {result.questions.map((q, idx) => (
                            <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{q.questionLabel}</span>
                              <ResultBadge result={q.result} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Azione eseguita */}
                      {result.action && (
                        <div className={`rounded-lg p-4 ${
                          result.action.performed
                            ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                            : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700'
                        }`}>
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                            {result.action.performed ? '✅ Azione Eseguita' : '⏸️ Azione in Sospeso'}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {result.action.message}
                          </p>
                          {result.action.suspensionExpiresAt && (
                            <p className="text-xs text-gray-500 mt-2">
                              Sospensione 2FA scade: {new Date(result.action.suspensionExpiresAt).toLocaleString('it-IT')}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Override (se non superata) */}
                      {!result.verification.approved && !result.verification.override?.enabled && (
                        <div className="border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 bg-yellow-50 dark:bg-yellow-900/20">
                          {!showOverride ? (
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                                  Override Manuale
                                </h4>
                                <p className="text-sm text-yellow-600 dark:text-yellow-400">
                                  Se ritieni che l'utente sia verificato nonostante il punteggio insufficiente
                                </p>
                              </div>
                              <button
                                onClick={() => setShowOverride(true)}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                              >
                                Applica Override
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                                Motivazione Override *
                              </h4>
                              <textarea
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder="Spiega perché stai approvando nonostante il punteggio insufficiente (min 10 caratteri)..."
                                className="w-full px-3 py-2 border border-yellow-300 dark:border-yellow-700 rounded-lg text-sm dark:bg-gray-800"
                                rows={3}
                              />
                              <div className="flex justify-end space-x-3">
                                <button
                                  onClick={() => setShowOverride(false)}
                                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                  Annulla
                                </button>
                                <button
                                  onClick={handleOverride}
                                  disabled={submitting || overrideReason.length < 10}
                                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center"
                                >
                                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                  Conferma Override
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Download PDF */}
                      <div className="flex justify-center">
                        <button
                          onClick={() => handleDownloadPDF(result.verification.reportId)}
                          disabled={downloadingPDF}
                          className="flex items-center px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                          {downloadingPDF ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Download in corso...
                            </>
                          ) : (
                            <>
                              <Download className="h-5 w-5 mr-2" />
                              Scarica Report PDF
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg mb-2">Nessun risultato</p>
                      <p className="text-sm">Completa la verifica nel tab "Verifica" per vedere il risultato</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Chiudi
          </button>

          {activeTab === 'verify' && !result && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifica in corso...
                </>
              ) : (
                <>
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Verifica e Resetta 2FA
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
